import React, {
  createRef, useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import {
  FaAddressBook, FaHospitalSymbol,
  FaCalendarAlt, FaRegClock, FaQuestionCircle, FaEllipsisH, FaPlusCircle,
  FaEye,
} from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { isIE } from 'react-device-detect';
import EpGlobalNavigation from '../../components/EpGlobalNavigation';
import EpLanguageModal from '../../components/EpLanguageModal';
import EpToaster from '../../components/EpToaster';
import EpSingleTableCard, { toggleColumn } from '../../components/EpSingleTableCard';
import EpBadge from '../../components/EpBadge';
import EpEmpty from '../../components/EpEmpty';
import EpFormInputIcon from '../../components/EpFormInputIcon';
import EpFormSelect, { clearSelectValue, cancelSelectValue } from '../../components/EpFormSelect';
import EpSelectPagination, { clearSelectPaginationValue, cancelSelectPaginationValue } from '../../components/EpSelectPagination';
import EpFormDate from '../../components/EpFormDate';
import EpFormRadio from '../../components/EpFormRadio';
import { signinRedirect } from '../../services/authService';
import integrationsService from '../../services/integrationsService';
import 'react-datepicker/dist/react-datepicker.css';
import './styles.scss';
import { USER_GET_HOSPITALS_ENDPOINT, USER_BASE_API_URL } from '../../constants/userConstants';
import useTranslation from '../../hooks/useTranslation';
import 'moment/locale/pt-br';
import EpButton from '../../components/EpButton';
import EpHelpModal from '../../components/EpHelpModal';
import EpErrorModal from '../../components/EpErrorModal';
import EpTooltip from '../../components/EpTooltip';
import { IDENTITY_NECESSARY_ROLES } from '../../constants/authConstants';
import EpSessionTimeOutModal from '../../components/EpSessionTimeOutModal';
import getDataTermAndTranslationsByTag from '../../utils/globalization';
import { handleToggleModal, handleToggleModalWithGlobalState } from '../../components/EpModal';
import { handleToggleModalStick } from '../../components/EpModalStick';
import EpTableDropdownOptions from '../../components/EpTableDropdownOptions';
import EpBrowserModal from '../../components/EpBrowserModal';

const IntegrationsAuditPage = () => {
  const currentUserLanguage = useSelector(state => state.globalization.language);

  // Tags do Globalization
  const Integrations = useTranslation('Integrations');
  const NoFiltersAppliedToGenerateAQuery = useTranslation('No filters applied to generate a query');
  const PleaseApplyFiltersToViewIntegrationLogs = useTranslation('Please, apply filters to view integration logs');

  const Select = useTranslation('Select');
  const Enter = useTranslation('enter');
  const RequiredField = useTranslation('* Required Fied');

  const TypeOfIntegration = useTranslation('Type of integration');
  const Hospital = useTranslation('Hospital');
  const PatientId = useTranslation('Patient ID');
  const Model = useTranslation('Model');
  const FirstStep = useTranslation('1st step (unfiltered)');
  const SecondStep = useTranslation('2nd step (filtered)');
  const SelectThePeriod = useTranslation('Select the Period');
  const SelectIntegration = useTranslation('Integrated?');
  const StartDate = useTranslation('Start date');
  const EndDate = useTranslation('End date');
  const Last7Days = useTranslation('Last 7 days');
  const Last30Days = useTranslation('Last 30 days');
  const Last3Months = useTranslation('Last 3 months');
  const OtherPeriod = useTranslation('Other period');

  const AwaitingImport = useTranslation('Awaiting import');
  const Processed = useTranslation('Processed');
  const Unprocessed = useTranslation('Unprocessed');
  const Yes = useTranslation('Yes');
  const No = useTranslation('No');

  const IntegrationLog = useTranslation('Integration Log');
  const UnitCode = useTranslation('Unit Code');
  const BedCode = useTranslation('Bed Code');

  const HospitalAdmissionDate = useTranslation('Hospital Admission Date');

  const ClientComorbidityID = useTranslation('Client Comorbidity ID');
  const VisitID = useTranslation('Visit ID');
  const IntegrationTypeID = useTranslation('Integration Type ID');
  const IntegrationStatus = useTranslation('Integration Status');
  const UnitAdmissionDate = useTranslation('Unit Admission Date.');
  const BedAdmissionDate = useTranslation('Bed Admission Date');
  const ClientComorbidityCode = useTranslation('Client Comorbidity Code');
  const ClientComorbidityEvent = useTranslation('Client Comorbidity Event');
  const ClientComorbiditySiteCode = useTranslation('Client Comorbidity Site Code');
  const EpimedComorbidityName = useTranslation('Epimed Comorbidity Name');
  const EpimedComorbiditySiteName = useTranslation('Epimed Comorbidity Site Name');
  const IntegratedOn = useTranslation('Integrated On');

  const ClientComplicationID = useTranslation('Client Complication ID');
  const Operation = useTranslation('Operation');
  const ClientComplicationCode = useTranslation('Client Complication Code');
  const EpimedComplicationName = useTranslation('Epimed Complication Name');
  const ComplicationDate = useTranslation('Complication Date');
  const ComplicationValue = useTranslation('Complication Value');
  const OneHourComplication = useTranslation('1 Hour Complication?');
  const TwentyFourHoursComplication = useTranslation('24 Hours Complication?');

  const ClientSAPS3ID = useTranslation('Client SAPS3 ID');
  const ClientSAPS3Code = useTranslation('Client SAPS3 Code');
  const ClientSAPS3Value = useTranslation('Client SAPS3 Value');
  const EpimedSAPS3Name = useTranslation('Epimed SAPS3 Name');

  const All = useTranslation('All');
  const Left = useTranslation('Left');
  const Right = useTranslation('Right');
  const ApplyFilters = useTranslation('Apply filters');

  const ClientInvasiveDeviceID = useTranslation('Client Invasive Device ID');
  const ClientInvasiveDeviceCode = useTranslation('Client Invasive Device Code');
  const ClientInvasiveDeviceSiteCode = useTranslation('Client Invasive Device Site Code');
  const ClientInvasiveDeviceLaterality = useTranslation('Client Invasive Device Laterality');
  const InvasiveDevicePlacementDate = useTranslation('Invasive Device Placement Date');
  const InvasiveDeviceRemovalDate = useTranslation('Invasive Device Removal Date');
  const EpimedInvasiveDeviceGroup = useTranslation('Epimed Invasive Device Group');
  const EpimedInvasiveDeviceType = useTranslation('Epimed Invasive Device Type');
  const EpimedInvasiveDeviceSite = useTranslation('Epimed Invasive Device Site');
  const EpimedInvasiveDeviceLaterality = useTranslation('Epimed Invasive Device Laterality');
  const RefinedErrorsCount = useTranslation('Refined Errors Count');

  const ClientUnitResourceID = useTranslation('Client Unit Resource ID');
  const ClientUnitResourceCode = useTranslation('Client Unit Resource Code');
  const ClientUnitResourceName = useTranslation('Client Unit Resource Name');
  const ClientUnitResourceValue = useTranslation('Client Unit Resource Value');
  const EpimedUnitResourceName = useTranslation('Epimed Unit Resource Name');

  const LogDetails = useTranslation('Log Details');
  const HospitalDischargeDate = useTranslation('Hospital Discharge Date');
  const HospitalDischargeCause = useTranslation('Hospital Discharge Cause');
  const ADTEvent = useTranslation('ADT Event');

  const Approved = useTranslation('Approved');
  const NotApproved = useTranslation('Not approved');

  const FirstHour = useTranslation('1h');
  const TwentyFourHour = useTranslation('24h');
  const Undefined = useTranslation('Undefined');

  const ClientExamCode = useTranslation('Client Exam Code');
  const ExamValue = useTranslation('Exam Value');
  const ExamUnitOfMeasure = useTranslation('Exam Unit Of Measure');
  const GatheringDate = useTranslation('Gathering Date');
  const EpimedExamName = useTranslation('Epimed Exam Name');
  const EpimedResultValue = useTranslation('Epimed Result Value');
  const EpimedLowestValue = useTranslation('Epimed Lowest Value');
  const EpimedHighestValue = useTranslation('Epimed Highest Value');

  const DateHour = useTranslation('Date / Hour');
  const ErrorName = useTranslation('Error Name');
  const ErrorDescription = useTranslation('Error Description');
  const ErrorDescriptionHistory = useTranslation('Error Description History');
  const RefinedErrorDescriptionHistory = useTranslation('Refined Error Description History');

  const PatientFlow = useTranslation('Patient Flow');
  // const LabMicro = useTranslation('Lab-Micro');
  const Diagnoses = useTranslation('Diagnoses');
  const Comorbities = useTranslation('Comorbities');
  const FirstAndTwentyFourHour = useTranslation('First and twenty four hour');
  const AcuteComplications = useTranslation('Acute Complications');
  const SAPS3 = useTranslation('SAPS3');
  const InvasiveDevices = useTranslation('Invasive devices');
  const UnitResources = useTranslation('Unit Resources');
  const Help = useTranslation('Help');
  const Options = useTranslation('Options');

  const CreateDate = useTranslation('Create Date');
  const ClientDiagnosisID = useTranslation('Client Diagnosis ID');
  const UpdateTimeStamp = useTranslation('Update Time Stamp');
  const ClientDiagnosisCode = useTranslation('Client Diagnosis Code');
  const OriginCode = useTranslation('Origin Code');
  const DiagnosisType = useTranslation('Diagnosis Type');
  const EpimedDiagnosisName = useTranslation('Epimed Diagnosis Name');
  const EventName = useTranslation('Event Name');
  const DiagnosisDate = useTranslation('Diagnosis Date');
  const IntegratedAsPrimary = useTranslation('Integrated as Primary?');
  const IntegratedAsSecondary = useTranslation('Integrated as Secondary?');
  const EpimedDiagnosisNameSecondary = useTranslation('Epimed Diagnosis Name Secondary');
  const LastProcessedOn = useTranslation('Last Processed On');
  const ErrorsCount = useTranslation('Errors Count');
  const DataImported = useTranslation('Data Imported?');

  //--------------------------------------------------------------------------------------------

  const history = useHistory();
  const user = useSelector(state => state.auth.user);
  let pageRendered = (<></>);

  //--------------------------------------------------------------------------------------------
  // ESTADOS DA TABELA
  const [itensPage, setItensPage] = useState(25);
  const [tableData, setTableData] = useState(null);
  const [tableColumns, setTableColumns] = useState([{ itens: null }]);
  const [integrationFuncData, setIntegrationFuncData] = useState(null);
  const [cardTitleIntegration, setCardTitleIntegration] = useState(Integrations);
  const [errorData, setErrorData] = useState(null);
  const [modalErrorTitle, setModalErrorTitle] = useState('');
  const [modalErrorSubtitle, setModalErrorSubtitle] = useState('');
  const [modalErrorCount, setModalErrorCount] = useState(0);
  const [modalErrorLoading, setModalErrorLoading] = useState(false);

  // COLUNAS DA TABELA
  const [columnsLogAdmissions, setColumnsLogAdmissions] = useState([]);
  // eslint-disable-next-line max-len
  const [columnsFirstAndTwentyFourHourFirstStep, setColumnsFirstAndTwentyFourHourFirstStep] = useState([]);
  // eslint-disable-next-line max-len
  const [columnsFirstAndTwentyFourHourSecondStep, setColumnsFirstAndTwentyFourHourSecondStep] = useState([]);
  const [columnsDiagnosis, setColumnsDiagnosis] = useState([]);
  const [columnsComorbities, setColumnsComorbities] = useState([]);
  const [columnsAcuteComplications, setColumnsAcuteComplications] = useState([]);
  const [columnsSaps3, setColumnsSaps3] = useState([]);
  const [columnsInvasiveDevice, setColumnsInvasiveDevice] = useState([]);
  const [columnsUnitResources, setColumnsUnitResources] = useState([]);
  const [columnsToHide, setColumnsToHide] = useState([]);
  const [lastIntegrationFiltered, setLastIntegrationFiltered] = useState('');
  const [integrationFuncName, setIntegrationFuncName] = useState('');
  const [columnsModalChecked, setColumnsModalChecked] = useState(true);

  const updateTimeStampRef = useRef();
  const epimedDiagnosisNameSecondaryRef = useRef();

  // Informações tabela do modal de Erro
  const [columnsErrorModal, setColumnsErrorModal] = useState([]);
  //--------------------------------------------------------------------------------------------

  // OPÇÕES DOS SELECTS DO FILTRO
  const [integrationOptions, setIntegrationOptions] = useState([]);
  const [integrationOptionsStatus, setIntegrationOptionsStatus] = useState([]);
  const periodOptions = [
    { value: 0, label: Last7Days },
    { value: 1, label: Last30Days },
    { value: 2, label: Last3Months },
    { value: 3, label: OtherPeriod }];

  const firstAndTwentyFourHourModelOptions = [
    { value: 0, label: FirstStep },
    { value: 1, label: SecondStep },
  ];

  //--------------------------------------------------------------------------------------------

  // ESTADOS DOS CAMPOS DE FILTRO
  const [integrationType, setIntegrationType] = useState(undefined);
  // 0 = All, 1 = AwaitingImport, 2 = Unprocessed, 3 = Processed, 4 = NotApproved
  const [integrationStatus, setIntegrationStatus] = useState(0);
  const [hospitalId, setHospitalId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [visitId, setVisitId] = useState('');
  const [period, setPeriod] = useState(undefined);
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [hospitalName, setHospitalName] = useState('');
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [firstHourComplication, setFirstHourComplication] = useState('');
  const [twentyFourHourComplication, setTwentyFourHourComplication] = useState('');
  const [searchingData, setSearchingData] = useState(false);
  // eslint-disable-next-line max-len
  const [firstAndTwentyFourHourModel, setFirstAndTwentyFourHourModel] = useState(0);

  const integrationTypeRef = createRef();
  const integrationStatusRef = createRef();
  const hospitalIdRef = createRef();
  const periodRef = createRef();
  const firstAndTwentyFourHourModelRef = createRef();

  // 1h / 24h Laboratory and Physiological Data = 2
  // Acute complications = 6
  const IGNORED_STATUS_INTEGRATION_TYPES = [2, 6];

  //--------------------------------------------------------------------------------------------

  // ESTADOS PARA SALVAR TEXTO DIGITADO NOS CAMPOS (FORMIK E LOCAL)
  const [hospitalInputEl, setHospitalInputEl] = useState('');
  const [hospitalInputValue, setHospitalInputValue] = useState('');
  const [patientIdEl, setPatientIdEl] = useState('');
  const [visitIdEl, setVisitIdEl] = useState('');
  //--------------------------------------------------------------------------------------------

  // Cancel do Filter (lastIntegrationTypeFiltered)
  const [cancelIntegrationType, setCancelIntegrationType] = useState('');
  const [cancelIntegrationStatus, setCancelIntegrationStatus] = useState(0);
  const [cancelHospitalId, setCancelHospitalId] = useState('');
  const [cancelHospitalName, setCancelHospitalName] = useState('');
  const [cancelPatientId, setCancelPatientId] = useState('');
  const [cancelVisitId, setCancelVisitId] = useState('');
  const [cancelPeriod, setCancelPeriod] = useState('');
  const [cancelStartDate, setCancelStartDate] = useState('');
  const [cancelEndDate, setCancelEndDate] = useState('');
  const [cancelFirstAndTwentyFourHourModel, setCancelFirstAndTwentyFourHourModel] = useState('');
  const [cancelFirstHourComplication, setCancelOneHourComplication] = useState(null);
  const [cancelTwentyFourHourComplication, setCancelTwentyFourHourComplication] = useState(null);

  // DECLARAÇÃO DOS USEREF DO FILTRO
  const filterFormRef = useRef();
  const [hospitalObject, setHospitalObject] = useState();
  const filterData = {
    integrationType,
    integrationStatus,
    hospitalId,
    patientId,
    visitId,
    startDate: startDate ? new Date(startDate).toISOString().split('T')[0] : '',
    endDate: endDate ? new Date(endDate).toISOString() : '',
    firstAndTwentyFourHourModel,
    firstHourComplication,
    twentyFourHourComplication,
  };
  //--------------------------------------------------------------------------------------------

  // DECLARAÇÃO DO YUP PARA VERIFICAÇÃO DOS CAMPOS DO FILTRO
  const filterSchema = Yup.object().shape({
    integrationType: Yup.number().required(RequiredField).nullable(),
    integrationStatus: Yup.number().nullable(),
    hospitalId: Yup.string().required(RequiredField).nullable(),
    patientId: Yup.string().nullable().notRequired(),
    visitId: Yup.string().nullable().notRequired(),
    period: Yup.number().required(RequiredField).nullable(),
    startDate: Yup.date().required(RequiredField).nullable(),
    endDate: Yup.date().required(RequiredField).nullable(),
    firstAndTwentyFourHourModel: Yup.number().nullable(),
    oneHourComplication: Yup.string().nullable(),
    twentyFourHourComplication: Yup.string().nullable(),
  });

  // REQUISIÇÃO DO MODAL DE ERROS
  const getErrorDataTable = async (
    getIntegrationFunc, integrationFuncNameParam, logId, integrationTypeId,
  ) => {
    let params = {};

    if (integrationFuncNameParam === 'getIntegrationLog1hAnd24hErrorsRefinedExamError') {
      params = {
        examId: logId,
        integrationTypeId,
        itensPerPage: 100,
      };
    } else {
      params = {
        logId,
        itensPerPage: 100,
      };
    }

    const responseJSON = await getIntegrationFunc(params);
    const integrationLogDiagnosisErrorsList = responseJSON ? responseJSON.response : null;

    setErrorData(integrationLogDiagnosisErrorsList);
    setModalErrorLoading(false);
    return errorData;
  };

  const getDataIntegrationLogDiagnosisErrors = async (
    integration, logId, integrationTypeId,
  ) => {
    setModalErrorLoading(true);
    const {
      getIntegrationLogDiagnosisErrors, getIntegrationLog1hAnd24hErrors,
      getIntegrationLog1hAnd24hErrorsRefinedExamError, getIntegrationLogComorbiditiesErrors,
      getIntegrationLogSAPS3Errors, getIntegrationLogUnitResourcesErrors,
      getIntegrationLogAcuteComplicationsErrors, getIntegrationLogInvasiveDeviceErrors,
      getIntegrationLogInvasiveDeviceErrorsRefined,
    } = integrationsService();

    switch (integration) {
    case 'diagnosisError':
      getErrorDataTable(
        getIntegrationLogDiagnosisErrors, 'getIntegrationLogDiagnosisErrors', logId, integrationTypeId,
      );
      break;
    case 'firstAndTwentyFourHourSFirstStepError':
      getErrorDataTable(
        getIntegrationLog1hAnd24hErrors, 'getIntegrationLog1hAnd24hErrors', logId, integrationTypeId,
      );
      break;
    case 'firstAndTwentyFourHourSecondStepError':
      getErrorDataTable(
        getIntegrationLog1hAnd24hErrorsRefinedExamError, 'getIntegrationLog1hAnd24hErrorsRefinedExamError', logId, integrationTypeId,
      );
      break;
    case 'comorbitiesError':
      getErrorDataTable(
        getIntegrationLogComorbiditiesErrors, 'getIntegrationLogComorbiditiesErrors', logId, integrationTypeId,
      );
      break;
    case 'saps3Error':
      getErrorDataTable(
        getIntegrationLogSAPS3Errors, 'getIntegrationLogSAPS3Errors', logId, integrationTypeId,
      );
      break;
    case 'unitResourcesError':
      getErrorDataTable(
        getIntegrationLogUnitResourcesErrors, 'getIntegrationLogUnitResourcesErrors', logId, integrationTypeId,
      );
      break;
    case 'acuteComplicationsError':
      getErrorDataTable(
        getIntegrationLogAcuteComplicationsErrors, 'getIntegrationLogAcuteComplicationsErrors', logId, integrationTypeId,
      );
      break;
    case 'invasiveDeviceError':
      getErrorDataTable(
        getIntegrationLogInvasiveDeviceErrors, 'getIntegrationLogInvasiveDeviceErrors', logId, integrationTypeId,
      );
      break;
    case 'invasiveDeviceRefinedError':
      getErrorDataTable(
        getIntegrationLogInvasiveDeviceErrorsRefined, 'getIntegrationLogInvasiveDeviceErrorsRefined', logId, integrationTypeId,
      );
      break;
    // TODO: Rever esse default
    default:
      console.log('selecione a integracao');
    }
  };
  // FUNÇÕES DA TABELA
  const showModalDiagnosisError = (cellInfo, integration) => {
    const { errorsCount } = cellInfo ? cellInfo.row.original : null;
    const refinedLogErrorsCount = cellInfo ? cellInfo.row.original.refinedLogErrorsCount : null;
    handleToggleModal('modal--integrations-error');

    if (integration === 'invasiveDevice') {
      setModalErrorTitle(RefinedErrorDescriptionHistory);
      setModalErrorSubtitle(RefinedErrorsCount);
      setModalErrorCount(refinedLogErrorsCount || 0);
      getDataIntegrationLogDiagnosisErrors(
        `${integration}RefinedError`,
        cellInfo.row.original.id || null,
      );

      return;
    }

    setModalErrorCount(errorsCount || 0);
    setModalErrorTitle(ErrorDescriptionHistory);
    setModalErrorSubtitle(ErrorsCount);
    if (integration === 'firstAndTwentyFourHourSecondStep') {
      getDataIntegrationLogDiagnosisErrors(
        `${integration}Error`,
        cellInfo.row.original.firstHourExamEpimedId || null,
        cellInfo.row.original.integrationTypeId || null,
      );
    } else {
      getDataIntegrationLogDiagnosisErrors(
        `${integration}Error`,
        cellInfo.row.original.id || null,
        undefined,
      );
    }
  };

  const badgeIntegratedCell = (integration, cellInfo) => {
    let result;
    const itegrationStatus = cellInfo ? cellInfo.row.original.integrationStatus : null;

    if (itegrationStatus !== null) {
      if (itegrationStatus === 1) {
        result = (<EpBadge color="warning">{AwaitingImport}</EpBadge>);
      } else if (itegrationStatus === 2) {
        result = (
          <div className="table__badge-intefrated-cell">
            <EpBadge color="danger">{Unprocessed}</EpBadge>
            <FaEye
              className="btn__icon"
              onClick={() => {
                showModalDiagnosisError(cellInfo, integration);
              }}
            />
          </div>
        );
      } else if (itegrationStatus === 3) {
        result = (<EpBadge color="success">{Processed}</EpBadge>);
      } else if (itegrationStatus === 4) {
        result = (<EpBadge color="new">{NotApproved}</EpBadge>);
      }
    } else {
      result = (<span>-</span>);
    }

    return result;
  };

  const emptyCell = ({ value }) => (
    value !== undefined && value !== null && value !== '' ? <span>{value}</span> : <span>-</span>
  );

  const timeCell = ({ value }) => (
    value !== undefined && value !== null && value !== '' ? (
      <>
        <span className="table__cell-date">
          {moment(value).format('DD-MM-YYYY')}
        </span>
        <span className="table__cell-time">
          <FaRegClock className="table__icon-clock" />
          {moment(value).locale('pt-BR').format('LTS')}
        </span>
      </>
    ) : <span>-</span>
  );

  const yesNoAsCell = ({ value }) => {
    let result;

    if (value !== undefined && value !== '') {
      if (value !== null && value !== false) {
        result = (<span className="table__cell-content">{Yes}</span>);
      } else {
        result = (<span className="table__cell-content">{No}</span>);
      }
    } else {
      result = (<span>-</span>);
    }

    return result;
  };

  const badgeYesNoCell = ({ value }) => {
    let result;

    if (value !== undefined && value !== '') {
      if (value !== null && value !== false) {
        result = (<EpBadge color="success">{Yes}</EpBadge>);
      } else {
        result = (<EpBadge color="danger">{No}</EpBadge>);
      }
    } else {
      result = (<span>-</span>);
    }

    return result;
  };

  const leftRightCell = ({ value }) => {
    let result;

    if (value !== undefined && value !== null && value !== '') {
      if (value === 1) {
        result = (<span className="table__cell-content">{Left}</span>);
      } else if (value === 2) {
        result = (<span className="table__cell-content">{Right}</span>);
      }
    } else {
      result = (<span>-</span>);
    }

    return result;
  };

  const refinedCell = ({ value }) => {
    let result;

    if (value !== undefined && value !== null && value !== '') {
      if (value === true) {
        result = (<EpBadge color="success">{Approved}</EpBadge>);
      } else if (value === false) {
        result = (<EpBadge color="danger">{NotApproved}</EpBadge>);
      }
    } else {
      result = (<span>-</span>);
    }

    return result;
  };

  const columnCellTextTruncation = ({ value }) => (
    value !== undefined && value !== null && value !== ''
      ? (
        <div className="table__cell-truncated">
          <p className="table__cell-truncated-text">{value}</p>
          <EpTooltip title={value} trigger="hover">
            <FaPlusCircle className="table__cell-truncated-icon" />
          </EpTooltip>
        </div>
      )
      : <span>-</span>
  );

  const integrationStatusBadge = (cellInfo) => {
    let cellBadge = (<span>-</span>);
    const statusId = cellInfo ? cellInfo.row.original.statusId : null;
    const statusText = cellInfo ? cellInfo.row.original.statusDescription : null;

    if (statusId !== undefined && statusId !== null && statusId !== '') {
      if (statusId === 1) {
        cellBadge = (<EpBadge color="default">{statusText}</EpBadge>);
      } else if (statusId === 2) {
        cellBadge = (<EpBadge color="warning">{statusText}</EpBadge>);
      } else if (statusId === 3) {
        cellBadge = (<EpBadge color="success">{statusText}</EpBadge>);
      } else if (statusId === 4) {
        cellBadge = (<EpBadge color="danger">{statusText}</EpBadge>);
      } else if (statusId === 5) {
        cellBadge = (<EpBadge color="warning">{statusText}</EpBadge>);
      } else if (statusId === 6) {
        cellBadge = (<EpBadge color="danger">{statusText}</EpBadge>);
      }
    }

    return cellBadge;
  };

  const integrationTypeIdCell = ({ value }) => {
    let result;

    if (value !== undefined && value !== '') {
      if (value === 1) {
        result = (<span className="table__cell-content">{FirstHour}</span>);
      } else if (value === 2) {
        result = (<span className="table__cell-content">{TwentyFourHour}</span>);
      } else if (value === null) {
        result = (<span className="table__cell-content">{Undefined}</span>);
      }
    } else {
      result = (<span>-</span>);
    }

    return result;
  };

  const boldCell = ({ value }) => (
    value !== undefined && value !== null && value !== '' ? <span className="table__cell--bold">{value}</span> : <span>-</span>
  );

  const errorDescriptionHistoryCell = (
    integration, cellInfo,
  ) => {
    let cellContent = (<span className="table__cell-error--empty">-</span>);

    cellContent = (
      <div className="dropdown">
        <EpButton
          actionType="button"
          buttonIcon={<FaEllipsisH className="btn__icon" />}
          buttonType="link-secondary"
          buttonId="button--options"
          dataBsToggle="dropdown"
          ariaExpanded="false"
        />
        <EpTableDropdownOptions
          ariaLabelledby="button--options"
          contentDropdown={
            (
              <>
                <EpButton
                  customClass="dropdown__menu-options-item"
                  labelText={ErrorDescriptionHistory}
                  buttonType="link-secondary"
                  actionType="button"
                  linkFunc={() => {
                    showModalDiagnosisError(cellInfo, integration);
                  }}
                  autoClose="true"
                />
                {integration === 'invasiveDevice'
                  && (
                    <EpButton
                      customClass="dropdown__menu-options-item"
                      labelText={RefinedErrorDescriptionHistory}
                      buttonTypef="link-secondary"
                      actionType="button"
                      linkFunc={() => {
                        showModalDiagnosisError(cellInfo, integration);
                      }}
                      autoClose="true"
                    />
                  )}
              </>
            )
          }
        />
      </div>
    );

    return cellContent;
  };

  useEffect(() => {
    // Mostra modal de sugestão para o usuário usar outro browser ao acessar o IE
    if (isIE && user) {
      // && sawIECompatibilityModalStatus !== 'true'
      handleToggleModalWithGlobalState('modal--browser-notification', 'IE_COMPATIBILITY', true, { backdrop: 'static' });
      // window.sessionStorage.setItem('sawIECompatibilityModal', 'true');
    }
  }, []);

  useMemo(() => {
    setCardTitleIntegration(Integrations);

    setColumnsLogAdmissions([
      {
        Header: CreateDate,
        accessor: 'createDateOn',
        Cell: timeCell,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: PatientId,
        accessor: 'patientId',
        Cell: emptyCell,
      },
      {
        Header: UnitCode,
        accessor: 'unitCode',
        Cell: emptyCell,
      },
      {
        Header: BedCode,
        accessor: 'epimedBedCode',
        Cell: emptyCell,
      },
      {
        Header: `${IntegrationStatus}`,
        accessor: 'statusDescription',
        Cell: cellInfo => integrationStatusBadge(cellInfo),
        minWidth: 260,
      },
      {
        Header: IntegrationLog,
        accessor: 'errorTypeDescription',
        Cell: emptyCell,
        minWidth: 240,
      },
      {
        Header: LogDetails,
        accessor: 'description',
        Cell: columnCellTextTruncation,
        minWidth: 260,
      },
      {
        Header: HospitalAdmissionDate,
        accessor: 'hospitalAdmissionDate',
        Cell: timeCell,
        minWidth: 180,
      },
      {
        Header: BedAdmissionDate,
        accessor: 'unitAdmissionDateTime',
        Cell: timeCell,
        minWidth: 200,
      },
      {
        Header: HospitalDischargeDate,
        accessor: 'hospitalDischargeDate',
        Cell: timeCell,
        minWidth: 190,
      },
      {
        Header: HospitalDischargeCause,
        accessor: 'dischargeCause',
        Cell: emptyCell,
        minWidth: 210,
      },
      {
        Header: ADTEvent,
        accessor: 'adtEvent',
        Cell: emptyCell,
      },
      {
        Header: LastProcessedOn,
        accessor: 'lastProcessedOn',
        Cell: timeCell,
        minWidth: 220,
      },

    ]);

    setColumnsFirstAndTwentyFourHourFirstStep([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        Cell: timeCell,
      },
      {
        Header: UpdateTimeStamp,
        accessor: 'updateTimeStamp',
        Cell: timeCell,
        hideColumnInitially: true,
        ref: updateTimeStampRef,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: IntegrationLog,
        accessor: 'refined',
        Cell: refinedCell,
        minWidth: 240,
      },
      {
        Header: ClientExamCode,
        accessor: 'clientExamCode',
        Cell: emptyCell,
      },
      {
        Header: ExamValue,
        accessor: 'clientExamtValue',
        Cell: emptyCell,
      },
      {
        Header: ExamUnitOfMeasure,
        accessor: 'clientExamUnitOfMeasure',
        Cell: emptyCell,
      },
      {
        Header: GatheringDate,
        accessor: 'clientGatheringDate',
        Cell: timeCell,
      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
      },
      {
        Header: Options,
        accessor: 'id',
        Cell: cellInfo => errorDescriptionHistoryCell('firstAndTwentyFourHourSFirstStep', cellInfo),
        minWidth: 180,
      },
    ]);

    setColumnsFirstAndTwentyFourHourSecondStep([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        Cell: timeCell,
      },
      {
        Header: ClientExamCode,
        accessor: 'clientExamCode',
        Cell: emptyCell,
      },
      {
        Header: UpdateTimeStamp,
        accessor: 'updateTimeStamp',
        Cell: timeCell,
        hideColumnInitially: true,
        ref: updateTimeStampRef,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: IntegrationTypeID,
        accessor: 'integrationTypeId',
        Cell: integrationTypeIdCell,
      },
      {
        Header: IntegrationStatus,
        accessor: 'integrationStatus',
        Cell: cellInfo => badgeIntegratedCell('firstAndTwentyFourHourSecondStep', cellInfo),
        minWidth: 260,
      },
      {
        Header: GatheringDate,
        accessor: 'epimedGatheringDate',
        Cell: timeCell,
      },
      {
        Header: EpimedExamName,
        accessor: 'epimedExamName',
        Cell: emptyCell,
      },
      {
        Header: EpimedResultValue,
        accessor: 'epimedResultValue',
        Cell: emptyCell,
      },
      {
        Header: EpimedLowestValue,
        accessor: 'epimedLowestValue',
        Cell: emptyCell,
      },
      {
        Header: EpimedHighestValue,
        accessor: 'epimedHighestValue',
        Cell: emptyCell,
      },
      {
        Header: LastProcessedOn,
        accessor: 'lastProcessedOn',
        Cell: timeCell,
      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
      },
      {
        Header: Options,
        accessor: 'firstHourExamEpimedId',
        Cell: cellInfo => errorDescriptionHistoryCell('firstAndTwentyFourHourSecondStep', cellInfo),
        minWidth: 180,
      },
    ]);

    setColumnsDiagnosis([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        // sticky: 'left', // Propriedade que deixa a coluna fixa
        Cell: timeCell,
      },
      {
        Header: ClientDiagnosisID,
        accessor: 'diagnosisId',
        Cell: emptyCell,
        minWidth: 180,
      },
      {
        Header: UpdateTimeStamp,
        accessor: 'diagnosisTimeStamp',
        Cell: timeCell,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: DataImported,
        accessor: 'integrationStatus',
        Cell: cellInfo => badgeIntegratedCell('diagnosis', cellInfo),
        minWidth: 260,
      },
      {
        Header: ClientDiagnosisCode,
        accessor: 'clientDiagnosisCode',
        Cell: emptyCell,
        minWidth: 210,
      },
      {
        Header: OriginCode,
        accessor: 'diagnosisOriginCode',
        Cell: emptyCell,
      },
      {
        Header: DiagnosisType,
        accessor: 'diagnosisTypeName',
        Cell: emptyCell,
      },
      {
        Header: EpimedDiagnosisName,
        accessor: 'epimedDiagnosisName',
        Cell: emptyCell,
        minWidth: 190,
      },
      {
        Header: EventName,
        accessor: 'messageControlEventName',
        Cell: emptyCell,
      },
      {
        Header: DiagnosisDate,
        accessor: 'diagnosisDate',
        Cell: timeCell,
      },
      {
        Header: IntegratedAsPrimary,
        accessor: 'unitAdmissionIdPrimary',
        Cell: yesNoAsCell,
        minWidth: 210,
      },
      {
        Header: IntegratedAsSecondary,
        accessor: 'unitAdmissionIdSecondary',
        Cell: yesNoAsCell,
        minWidth: 220,
      },
      {
        Header: EpimedDiagnosisNameSecondary,
        accessor: 'epimedDiagnosisNameSecondary',
        Cell: emptyCell,
        minWidth: 220,
        hideColumnInitially: true,
        ref: epimedDiagnosisNameSecondaryRef,
      },
      {
        Header: LastProcessedOn,
        accessor: 'lastProcessedOn',
        Cell: timeCell,
        minWidth: 160,
      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
      },
      {
        Header: Options,
        accessor: 'id',
        Cell: cellInfo => errorDescriptionHistoryCell('diagnosis', cellInfo),
        minWidth: 180,
      },
    ]);

    setColumnsComorbities([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        // sticky: 'left', // Propriedade que deixa a coluna fixa
        Cell: timeCell,
      },
      {
        Header: ClientComorbidityID,
        accessor: 'comorbidityId',
        Cell: emptyCell,
        minWidth: 190,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: IntegrationStatus,
        accessor: 'integrationStatus',
        Cell: cellInfo => badgeIntegratedCell('comorbities', cellInfo),
        minWidth: 260,
      },
      {
        Header: UnitAdmissionDate,
        accessor: 'unitAdmissionDatetime',
        Cell: timeCell,
        minWidth: 210,
      },
      {
        Header: ClientComorbidityCode,
        accessor: 'clientComorbidityCode',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: ClientComorbidityEvent,
        accessor: 'comorbidityValueDescription',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: ClientComorbiditySiteCode,
        accessor: 'clientComorbiditySiteCode',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: EpimedComorbidityName,
        accessor: 'comorbidityLibraryName',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: EpimedComorbiditySiteName,
        accessor: 'comorbidityComplementName',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: LastProcessedOn,
        accessor: 'lastProcessedOn',
        Cell: timeCell,
        minWidth: 160,
      },
      {
        Header: IntegratedOn,
        accessor: 'integratedOn',
        Cell: timeCell,
        minWidth: 160,
      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
        minWidth: 160,
      },
      {
        Header: Options,
        accessor: 'id',
        Cell: cellInfo => errorDescriptionHistoryCell('comorbities', cellInfo),
        minWidth: 180,
        width: 180,
      },
    ]);

    setColumnsAcuteComplications([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        // sticky: 'left', // Propriedade que deixa a coluna fixa
        Cell: timeCell,
      },
      {
        Header: ClientComplicationID,
        accessor: 'clientAcuteComplicationRecordId',
        Cell: emptyCell,
        minWidth: 190,
      },
      {
        Header: UpdateTimeStamp,
        accessor: 'clientUpdateTimeStamp',
        Cell: timeCell,

      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,

      },
      {
        Header: Operation,
        accessor: 'operationTypeName',
        Cell: emptyCell,

      },
      {
        Header: ClientComplicationCode,
        accessor: 'clientAcuteComplicationCode',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: ComplicationValue,
        accessor: 'clientAcuteComplicationValue',
        Cell: emptyCell,
      },
      {
        Header: EpimedComplicationName,
        accessor: 'acuteComplicationTypeName',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: ComplicationDate,
        accessor: 'complicationDate',
        Cell: timeCell,
      },
      {
        Header: OneHourComplication,
        accessor: 'unitAdmissionId1h',
        Cell: badgeYesNoCell,
      },
      {
        Header: TwentyFourHoursComplication,
        accessor: 'unitAdmissionId24h',
        Cell: badgeYesNoCell,
      },
      {
        Header: LastProcessedOn,
        accessor: 'lastProcessedOn',
        Cell: timeCell,
        minWidth: 160,
        width: 160,
      },
      {
        Header: IntegratedOn,
        accessor: 'integratedOn',
        Cell: timeCell,
        minWidth: 160,
        width: 160,
      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
        minWidth: 160,
        width: 160,
      },
      {
        Header: Options,
        accessor: 'id',
        Cell: cellInfo => errorDescriptionHistoryCell('acuteComplications', cellInfo),
        minWidth: 180,
        width: 180,
      },
    ]);

    setColumnsSaps3([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        // sticky: 'left', // Propriedade que deixa a coluna fixa
        Cell: timeCell,
      },
      {
        Header: ClientSAPS3ID,
        accessor: 'clientRecordId',
        Cell: emptyCell,
        minWidth: 160,
      },
      {
        Header: UpdateTimeStamp,
        accessor: 'clientUpdateTimeStamp',
        Cell: timeCell,
        minWidth: 160,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: IntegrationStatus,
        accessor: 'integrationStatus',
        Cell: cellInfo => badgeIntegratedCell('saps3', cellInfo),
        minWidth: 260,
      },
      {
        Header: UnitAdmissionDate,
        accessor: 'unitAdmissionDate',
        Cell: timeCell,
        minWidth: 220,
      },
      {
        Header: Operation,
        accessor: 'operationTypeName',
        Cell: emptyCell,
      },
      {
        Header: ClientSAPS3Code,
        accessor: 'clientSaps3Code',
        Cell: emptyCell,
        minWidth: 180,
      },
      {
        Header: ClientSAPS3Value,
        accessor: 'clientSaps3Value',
        Cell: emptyCell,
        minWidth: 170,
      },
      {
        Header: EpimedSAPS3Name,
        accessor: 'saps3AdmissionName',
        Cell: emptyCell,
        minWidth: 170,
      },
      {
        Header: LastProcessedOn,
        accessor: 'lastProcessedOn',
        Cell: timeCell,
        minWidth: 180,
      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
        minWidth: 160,
      },
      {
        Header: Options,
        accessor: 'id',
        Cell: cellInfo => errorDescriptionHistoryCell('saps3', cellInfo),
        minWidth: 180,
        width: 180,
      },
    ]);

    setColumnsInvasiveDevice([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        // sticky: 'left', // Propriedade que deixa a coluna fixa
        Cell: timeCell,
      },
      {
        Header: ClientInvasiveDeviceID,
        accessor: 'clientInvasiveDeviceId',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: IntegrationStatus,
        accessor: 'integrationStatus',
        Cell: cellInfo => badgeIntegratedCell('invasiveDevice', cellInfo),
        minWidth: 260,
      },
      {
        Header: Operation,
        accessor: 'operationTypeName',
        Cell: emptyCell,
      },
      {
        Header: ClientInvasiveDeviceCode,
        accessor: 'clientInvasiveDeviceCode',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: ClientInvasiveDeviceSiteCode,
        accessor: 'clientInvasiveDeviceSiteCode',
        Cell: emptyCell,
        minWidth: 275,
      },
      {
        Header: ClientInvasiveDeviceLaterality,
        accessor: 'clientLateralityCode',
        Cell: emptyCell,
        minWidth: 275,

      },
      {
        Header: InvasiveDevicePlacementDate,
        accessor: 'placementDate',
        Cell: timeCell,
        minWidth: 240,

      },
      {
        Header: InvasiveDeviceRemovalDate,
        accessor: 'removalDate',
        Cell: timeCell,
        minWidth: 220,

      },
      {
        Header: EpimedInvasiveDeviceGroup,
        accessor: 'epimedInvasiveDeviceGroupName',
        Cell: emptyCell,
        minWidth: 280,

      },
      {
        Header: EpimedInvasiveDeviceType,
        accessor: 'epimedInvasiveDeviceTypeName',
        Cell: emptyCell,
        minWidth: 200,
      },
      {
        Header: EpimedInvasiveDeviceSite,
        accessor: 'epimedInvasiveDeviceSiteName',
        Cell: emptyCell,
        minWidth: 230,

      },
      {
        Header: EpimedInvasiveDeviceLaterality,
        accessor: 'refinedLogLateralityCode',
        Cell: leftRightCell,
        minWidth: 260,

      },
      {
        Header: LastProcessedOn,
        accessor: 'refinedLogLastProcessedOn',
        Cell: timeCell,
        minWidth: 160,

      },
      {
        Header: IntegratedOn,
        accessor: 'refinedLogIntegratedOn',
        Cell: timeCell,
        minWidth: 160,

      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
        minWidth: 160,

      },
      {
        Header: RefinedErrorsCount,
        accessor: 'refinedLogErrorsCount',
        Cell: emptyCell,
        minWidth: 160,

      },
      {
        Header: Options,
        accessor: 'id',
        Cell: cellInfo => errorDescriptionHistoryCell('invasiveDevice', cellInfo),
        minWidth: 180,
        width: 180,
      },
    ]);

    setColumnsUnitResources([
      {
        Header: CreateDate,
        accessor: 'createdOn',
        // sticky: 'left', // Propriedade que deixa a coluna fixa
        Cell: timeCell,
      },
      {
        Header: ClientUnitResourceID,
        accessor: 'clientUnitResourceRecordId',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: UpdateTimeStamp,
        accessor: 'clientUpdateTimeStamp',
        Cell: timeCell,
      },
      {
        Header: VisitID,
        accessor: 'visitId',
        Cell: emptyCell,
      },
      {
        Header: IntegrationStatus,
        accessor: 'integrationStatus',
        Cell: cellInfo => badgeIntegratedCell('unitResources', cellInfo),
        minWidth: 260,
      },
      {
        Header: UnitAdmissionDate,
        accessor: 'unitAdmissionDate',
        Cell: timeCell,
        minWidth: 220,
      },
      {
        Header: Operation,
        accessor: 'operationTypeName',
        Cell: emptyCell,
      },
      {
        Header: ClientUnitResourceCode,
        accessor: 'clientUnitResourceCode',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: ClientUnitResourceName,
        accessor: 'clientUnitResourceName',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: ClientUnitResourceValue,
        accessor: 'clientUnitResourceValue',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: EpimedUnitResourceName,
        accessor: 'epimedUnitResourceTypeName',
        Cell: emptyCell,
        minWidth: 220,
      },
      {
        Header: LastProcessedOn,
        accessor: 'lastProcessedOn',
        Cell: timeCell,
        minWidth: 200,
      },
      {
        Header: IntegratedOn,
        accessor: 'integratedOn',
        Cell: timeCell,
      },
      {
        Header: ErrorsCount,
        accessor: 'errorsCount',
        Cell: emptyCell,
      },
      {
        Header: Options,
        accessor: 'id',
        Cell: cellInfo => errorDescriptionHistoryCell('unitResources', cellInfo),
        minWidth: 180,
        width: 180,
      },
    ]);

    setColumnsErrorModal([
      {
        Header: DateHour,
        accessor: 'lastSyncDate',
        Cell: timeCell,
      },
      {
        Header: ErrorName,
        accessor: 'errorTypeName',
        Cell: boldCell,
      },
      {
        Header: ErrorDescription,
        accessor: 'errorTypeDescription',
        Cell: emptyCell,
        minWidth: 432,
      },
    ]);
    // eslint-disable-next-line max-len
  }, [DataImported]); // TODO: Colocar uma variação de estado mais correta aqui, não devemos ficar refens de um termo

  //--------------------------------------------------------------------------------------------

  // REQUISIÇÕES DA TABELA
  const getDataTable = async (page, itensPerPage, filter, getIntegrationFunc, columns,
    cardTitle) => {
    setSearchingData(true);
    setItensPage(itensPerPage);
    setTableColumns(columns);
    setIntegrationFuncData(() => getIntegrationFunc);

    let initialParams = {
      page,
      itensPerPage,
      hospitalId: filter.hospitalId || null,
      startDate: filter.startDate || null,
      finishDate: filter.endDate || null,
      integrationStatus: filter.integrationStatus || null,
    };

    if (filter.integrationType === 1) {
      initialParams = {
        ...initialParams,
        patientId: filter.patientId || null,
      };
    }

    if (filter.firstHourComplication !== '') {
      initialParams = {
        ...initialParams,
        firstHourComplication: filter.firstHourComplication,
      };
    }

    if (filter.twentyFourHourComplication !== '') {
      initialParams = {
        ...initialParams,
        twentyFourHourComplication: filter.twentyFourHourComplication,
      };
    }

    if (filter.integrationType === 2) {
      initialParams = {
        ...initialParams,
        firstAndTwentyFourHourModel: filter.firstAndTwentyFourHourModel,
      };
    }

    if (filter.integrationType === 1
      || filter.integrationType === 2
      || filter.integrationType === 4
      || filter.integrationType === 5
      || filter.integrationType === 6
      || filter.integrationType === 7
      || filter.integrationType === 8
      || filter.integrationType === 9) {
      initialParams = {
        ...initialParams,
        visitId: filter.visitId || null,
      };
    }

    const responseJSON = await getIntegrationFunc(initialParams);

    const tableDataList = responseJSON ? responseJSON.response : null;

    setTableData(tableDataList);
    setSearchingData(false);
    // eslint-disable-next-line no-unused-expressions
    cardTitle !== undefined && setCardTitleIntegration(cardTitle);

    return { tableData, tableColumns };
  };

  //--------------------------------------------------------------------------------------------
  // GET DAS OPÇÕES DO INTEGRATIONS
  const getDataIntegrationOptions = async () => {
    const responseJSON = await integrationsService().getIntegrationTypes();
    const integrationTypesList = responseJSON ? responseJSON.response : null;

    if (integrationTypesList) {
      const typesList = integrationTypesList.map((option) => {
        const optionsList = {
          value: option.id,
          label: option.name,
        };

        return optionsList;
      });
      setIntegrationOptions(typesList);
    }

    return integrationOptions;
  };

  const getDataIntegrationStatusOptions = useCallback(async (idIntegration) => {
    const responseJSON = await integrationsService().getIntegrationStatus({
      typeIdIntegration: idIntegration,
      culture: currentUserLanguage,
    });
    const integrationStatusList = responseJSON ? responseJSON.response : null;

    if (integrationStatusList) {
      const integrationStatusListWithAll = [{
        statusId: 0,
        statusName: All,
      }, ...integrationStatusList];

      const listOptionsStatus = integrationStatusListWithAll.map((option) => {
        const optionsList = {
          value: option.statusId,
          label: option.statusName,
        };

        return optionsList;
      }).sort((a, b) => {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      });

      setIntegrationOptionsStatus(listOptionsStatus);
    }
    return integrationOptionsStatus;
  }, [currentUserLanguage, All]);

  useEffect(() => {
    const isIgnoredIntegrationType = IGNORED_STATUS_INTEGRATION_TYPES.includes(integrationType);

    if (integrationType && !isIgnoredIntegrationType) {
      getDataIntegrationStatusOptions(integrationType);
    }
  }, [integrationType, currentUserLanguage]);

  // REQUISIÇÃO DOS FILTROS
  const handleFilterSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    setCurrentPageNumber(1);
    setCancelIntegrationType(filterData.integrationType);
    setCancelIntegrationStatus(filterData.integrationStatus);
    setCancelHospitalId(filterData.hospitalId);
    setCancelHospitalName(hospitalName);
    setHospitalObject({ value: filterData.hospitalId, label: hospitalName });
    setCancelPatientId(filterData.patientId);
    setCancelVisitId(filterData.visitId);
    setCancelPeriod(period);
    setCancelStartDate(filterData.startDate);
    setCancelEndDate(filterData.endDate);
    setCancelFirstAndTwentyFourHourModel(filterData.firstAndTwentyFourHourModel);
    setCancelOneHourComplication(filterData.oneHourComplication);
    setCancelTwentyFourHourComplication(filterData.twentyFourHourComplication);

    if (filterFormRef.current) {
      filterFormRef.current.handleSubmit();
    }

    if (filterFormRef.current.isValid === true && filterData.integrationType !== null
      && filterData.integrationType !== undefined && filterData.integrationType) {
      handleToggleModalStick('button__dropdown-filters');

      const {
        getIntegrationLogAdmissions, getIntegrationLog1hAnd24h,
        getIntegrationLog1hAnd24hErrorsRefined, getIntegrationLogLabMicro,
        getIntegrationLogDiagnosis, getIntegrationLogComorbidities,
        getIntegrationLogAcuteComplications, getIntegrationLogSAPS3,
        getIntegrationLogInvasiveDevice, getIntegrationLogUnitResources,
      } = integrationsService();

      switch (filterData.integrationType) {
      case 1:
        getDataTable(0, itensPage, filterData, getIntegrationLogAdmissions, columnsLogAdmissions,
          PatientFlow);
        setIntegrationFuncName('getIntegrationLogAdmissions');
        toggleColumn(columnsLogAdmissions, columnsToHide, setColumnsToHide, 'getIntegrationLogAdmissions', lastIntegrationFiltered, setLastIntegrationFiltered);
        break;
      case 2:
        if (filterData.firstAndTwentyFourHourModel === 0) {
          getDataTable(0, itensPage, filterData, getIntegrationLog1hAnd24h,
            columnsFirstAndTwentyFourHourFirstStep, FirstAndTwentyFourHour);
          setIntegrationFuncName('getIntegrationLog1hAnd24h');

          toggleColumn(columnsFirstAndTwentyFourHourFirstStep, columnsToHide, setColumnsToHide, 'getIntegrationLog1hAnd24h', lastIntegrationFiltered, setLastIntegrationFiltered);
        } else if (filterData.firstAndTwentyFourHourModel === 1) {
          getDataTable(0, itensPage, filterData, getIntegrationLog1hAnd24hErrorsRefined,
            columnsFirstAndTwentyFourHourSecondStep, FirstAndTwentyFourHour);
          setIntegrationFuncName('getIntegrationLog1hAnd24hErrorsRefined');
          toggleColumn(columnsFirstAndTwentyFourHourSecondStep, columnsToHide, setColumnsToHide, 'getIntegrationLog1hAnd24hErrorsRefined', lastIntegrationFiltered, setLastIntegrationFiltered);
        }
        break;
      case 3:
        getDataTable(0, itensPage, filterData, getIntegrationLogLabMicro);
        // adicionar o LabMicro no getDataTable
        // setIntegrationFuncName('getIntegrationLogAdmissions');
        // toggleColumn(columnsLogAdmissions, columnsToHide,
        // setColumnsToHide, 'getIntegrationLogLabMicro');
        break;
      case 4:
        getDataTable(0, itensPage, filterData, getIntegrationLogDiagnosis, columnsDiagnosis,
          Diagnoses);
        setIntegrationFuncName('getIntegrationLogDiagnosis');
        toggleColumn(columnsDiagnosis, columnsToHide, setColumnsToHide, 'getIntegrationLogDiagnosis', lastIntegrationFiltered, setLastIntegrationFiltered, setColumnsModalChecked);
        break;
      case 5:
        getDataTable(0, itensPage, filterData, getIntegrationLogComorbidities, columnsComorbities,
          Comorbities);
        setIntegrationFuncName('getIntegrationLogComorbidities');
        toggleColumn(columnsComorbities, columnsToHide, setColumnsToHide, 'getIntegrationLogComorbidities', lastIntegrationFiltered, setLastIntegrationFiltered, setColumnsModalChecked);
        break;
      case 6:
        getDataTable(0, itensPage, filterData, getIntegrationLogAcuteComplications,
          columnsAcuteComplications, AcuteComplications);
        setIntegrationFuncName('getIntegrationLogAcuteComplications');
        toggleColumn(columnsAcuteComplications, columnsToHide, setColumnsToHide, 'getIntegrationLogAcuteComplications', lastIntegrationFiltered, setLastIntegrationFiltered);
        break;
      case 7:
        getDataTable(0, itensPage, filterData, getIntegrationLogSAPS3, columnsSaps3, SAPS3);
        setIntegrationFuncName('getIntegrationLogSAPS3');
        toggleColumn(columnsSaps3, columnsToHide, setColumnsToHide, 'getIntegrationLogSAPS3', lastIntegrationFiltered, setLastIntegrationFiltered);
        break;
      case 8:
        getDataTable(0, itensPage, filterData, getIntegrationLogInvasiveDevice,
          columnsInvasiveDevice, InvasiveDevices);
        setIntegrationFuncName('getIntegrationLogInvasiveDevice');
        toggleColumn(columnsInvasiveDevice, columnsToHide, setColumnsToHide, 'getIntegrationLogInvasiveDevice', lastIntegrationFiltered, setLastIntegrationFiltered);
        break;
      case 9:
        getDataTable(0, itensPage, filterData, getIntegrationLogUnitResources, columnsUnitResources,
          UnitResources);
        setIntegrationFuncName('getIntegrationLogUnitResources');
        toggleColumn(columnsUnitResources, columnsToHide, setColumnsToHide, 'getIntegrationLogUnitResources', lastIntegrationFiltered, setLastIntegrationFiltered);
        break;
        // TODO: Rever esse default
      default:
        console.log('selecione a integracao');
      }
    }
  };

  useEffect(() => {
    if (cancelIntegrationType !== '' && cancelIntegrationType) {
      handleFilterSubmit(null);
    }
  }, [DataImported]);

  // TROCAR DAS DATAS A PARTIR DO SELECT DE PERIOD
  const changeDateInPeriodSelection = (selectedPeriod, setFieldValueFunc) => {
    const today = moment.utc(new Date());
    setPeriod(selectedPeriod);
    if (selectedPeriod === 0) {
      const periodDate = moment.utc(today).startOf('day').add(-7, 'days');
      setStartDate(periodDate);
      setEndDate(today);
      setFieldValueFunc('startDate', moment.utc(periodDate).startOf('day'));
      setFieldValueFunc('endDate', moment.utc(today).endOf('day'));
    } else if (selectedPeriod === 1) {
      const periodDate = moment.utc(today).startOf('day').add(-30, 'days');
      setStartDate(periodDate);
      setEndDate(today);
      setFieldValueFunc('startDate', moment.utc(periodDate).startOf('day'));
      setFieldValueFunc('endDate', moment.utc(today).endOf('day'));
    } else if (selectedPeriod === 2) {
      const periodDate = moment.utc(today).startOf('day').add(-90, 'days');
      setStartDate(periodDate);
      setEndDate(today);
      setFieldValueFunc('startDate', moment.utc(periodDate).startOf('day'));
      setFieldValueFunc('endDate', moment.utc(today).endOf('day'));
    } else if (selectedPeriod === 3) {
      setStartDate(undefined);
      setEndDate(undefined);
      setFieldValueFunc('startDate', undefined);
      setFieldValueFunc('endDate', undefined);
    }
  };

  // SETANDO CONTEUDO DE TEXTO DOS CAMPOS DO FILTRO
  const handleHospitalInputChange = () => {
    if (hospitalInputEl !== null && hospitalInputEl !== undefined && hospitalInputEl !== '') {
      setHospitalInputValue(hospitalInputEl[0].value);
    }
  };

  const handlepatientIdInputChange = () => {
    if (patientIdEl !== null && patientIdEl !== undefined && patientIdEl !== '') {
      setPatientId(patientIdEl.value);
    }
  };

  const handleHospitalAdmissionNumberInputChange = () => {
    if (visitIdEl !== null && visitIdEl !== undefined && visitIdEl !== '') {
      setVisitId(visitIdEl.value);
    }
  };
  useEffect(() => {
    if (user) {
      const necessaryRolesArr = IDENTITY_NECESSARY_ROLES;
      let hasNecessaryHole = false;

      if (user && Array.isArray(user.role)) {
        hasNecessaryHole = user ? user.role.some(role => necessaryRolesArr.includes(role)) : false;
      } else if (user) {
        hasNecessaryHole = user ? necessaryRolesArr.includes(user.role) : false;
      }

      if (hasNecessaryHole) {
        getDataIntegrationOptions();
        setHospitalInputEl(document.getElementById('hospital-select')
          .querySelectorAll('input'));
        setPatientIdEl(document.getElementById('patientId-textfield'));
        setVisitIdEl(document.getElementById('visitId-textfield'));
      } else {
        history.push({
          pathname: '/shared/error',
          state: { code: 401 }, // , message
        });
      }
    } else {
      pageRendered = signinRedirect();
    }
  }, [user]);

  const resetFilterForm = () => {
    if (filterFormRef.current) {
      filterFormRef.current.handleReset();

      clearSelectValue(integrationTypeRef);
      clearSelectPaginationValue(hospitalIdRef);
      clearSelectValue(periodRef);
    }

    filterFormRef.current.values = {
      integrationType: '',
      integrationStatus: '',
      hospitalId: '',
      patientId: '',
      visitId: '',
      period: '',
      startDate: '',
      endDate: '',
      firstAndTwentyFourHourModel: 0,
      hospitalName: '',
      oneHourComplication: '',
      twentyFourHourComplication: '',
    };

    setIntegrationType('');
    setIntegrationStatus(0);
    setHospitalId('');
    setHospitalName('');
    setPatientId('');
    setVisitId('');
    setPeriod('');
    setStartDate('');
    setEndDate('');
    setFirstAndTwentyFourHourModel(0);
    setFirstHourComplication('');
    setTwentyFourHourComplication('');
  };

  const cancelFilterForm = () => {
    if (filterFormRef.current) {
      cancelSelectValue(integrationTypeRef, cancelIntegrationType);
      cancelSelectValue(integrationStatusRef, cancelIntegrationStatus);
      cancelSelectValue(periodRef, cancelPeriod);
      cancelSelectValue(firstAndTwentyFourHourModelRef, cancelFirstAndTwentyFourHourModel);
      cancelSelectPaginationValue(hospitalIdRef, hospitalObject);
    }

    filterFormRef.current.values = {
      integrationType: cancelIntegrationType,
      integrationStatus: cancelIntegrationStatus,
      hospitalId: cancelHospitalId,
      period: cancelPeriod,
      patientId: cancelPatientId,
      visitId: cancelVisitId,
      startDate: cancelStartDate,
      endDate: cancelEndDate,
      firstAndTwentyFourHourModel: cancelFirstAndTwentyFourHourModel,
      firstHourComplication: cancelFirstHourComplication,
      twentyFourHourComplication: cancelTwentyFourHourComplication,
    };

    setIntegrationType(cancelIntegrationType);
    setIntegrationStatus(cancelIntegrationStatus);
    setHospitalId(cancelHospitalId);
    setHospitalName(cancelHospitalName);
    setHospitalObject({ value: cancelHospitalId, label: cancelHospitalName });
    setPeriod(cancelPeriod);
    setVisitId(cancelVisitId);
    setStartDate(cancelStartDate);
    setEndDate(cancelEndDate);
    setPatientId(cancelPatientId);
    setFirstAndTwentyFourHourModel(cancelFirstAndTwentyFourHourModel);
    setFirstHourComplication(cancelFirstHourComplication);
    setTwentyFourHourComplication(cancelTwentyFourHourComplication);
  };

  // 1h / 24h Laboratory and Physiological Data = 2
  // Acute complications = 6
  const showSelectIntegrationStatus = integrationType && integrationType !== ''
  && !IGNORED_STATUS_INTEGRATION_TYPES.includes(integrationType);

  const filterBodyContent = (
    <Formik
      innerRef={filterFormRef}
      enableReinitialize
      initialValues={{
        integrationType,
        integrationStatus,
        hospitalId,
        patientId,
        visitId,
        period,
        startDate,
        endDate,
        firstAndTwentyFourHourModel,
        hospitalName,
        firstHourComplication,
        twentyFourHourComplication,

      }}
      validationSchema={filterSchema}
    >
      {({
        values, errors, touched, setFieldTouched,
        setFieldValue, handleChange, setFieldError,
        // handleSubmit,
      }) => (
        <form
          className="form-filters"
          onSubmit={(e) => { handleFilterSubmit(e); }}
        >
          <div className="row">
            <div className="mb-3">
              <EpFormSelect
                customRef={integrationTypeRef}
                classError={errors.integrationType && touched.integrationType ? 'is-invalid' : ''}
                optionsSelect={integrationOptions.sort((a, b) => {
                  if (a.label < b.label) return -1;
                  if (a.label > b.label) return 1;
                  return 0;
                })}
                inputId="integrationType-select"
                placeholderText={Select}
                inputName="integrationType"
                value={values.integrationType}
                onChange={(val) => {
                  setFieldValue('integrationType', val.value !== undefined ? val.value : val);
                  setIntegrationType(val.value !== undefined ? val.value : val);
                  setIntegrationStatus(0);
                }}
                onBlur={() => setFieldTouched('integrationType', true)}
                labelText={TypeOfIntegration}
              />
              {errors.integrationType && touched.integrationType ? <div className="error-message">{errors.integrationType}</div> : null}
            </div>
          </div>

          {
            (showSelectIntegrationStatus) ? (
              <div className="row">
                <div className="mb-3">
                  <EpFormSelect
                    customRef={integrationStatusRef}
                    classError={errors.integrationStatus && touched.integrationStatus ? 'is-invalid' : ''}
                    onBlur={() => setFieldTouched('integrationStatus', true)}
                    optionsSelect={integrationOptionsStatus}
                    inputId="integrationStatus-select"
                    placeholderText={Select}
                    inputName="integrationStatus"
                    value={values.integrationStatus}
                    onChange={(val) => {
                      setFieldValue('integrationStatus', val.value !== undefined ? val.value : val);
                      setIntegrationStatus(val.value !== undefined ? val.value : val);
                    }}
                    labelText={SelectIntegration}
                    inputIcon={<FaCalendarAlt />}
                  />
                  {
                    errors.integrationStatus && touched.integrationStatus
                      ? <div className="error-message">{errors.integrationStatus}</div>
                      : null
                  }
                </div>
              </div>
            ) : null
          }

          {values.integrationType === 6 ? (
            <div className="row mb-2">
              <div className="col-4">
                <EpFormRadio.Wrap labelTitle="1 hora">
                  <EpFormRadio.Input
                    labelText="Sim"
                    inputId="1hour-yes"
                    inputName="1hour"
                    checked={values.firstHourComplication === 'true'}
                    onChange={() => {
                      setFieldValue('firstHourComplication', 'true');
                      setFirstHourComplication('true');
                    }}
                  />
                  <EpFormRadio.Input
                    labelText="Não"
                    inputId="1hour-no"
                    inputName="1hour"
                    checked={values.firstHourComplication === 'false'}
                    onChange={() => {
                      setFieldValue('firstHourComplication', 'false');
                      setFirstHourComplication('false');
                    }}
                  />
                </EpFormRadio.Wrap>
              </div>
              <div className="col-4">
                <EpFormRadio.Wrap labelTitle="24 hora">
                  <EpFormRadio.Input
                    labelText="Sim"
                    inputId="24hour-yes"
                    inputName="24hour"
                    checked={values.twentyFourHourComplication === 'true'}
                    onChange={() => {
                      setFieldValue('twentyFourHourComplication', 'true');
                      setTwentyFourHourComplication('true');
                    }}
                  />
                  <EpFormRadio.Input
                    labelText="Não"
                    inputId="24hour-no"
                    inputName="24hour"
                    checked={values.twentyFourHourComplication === 'false'}
                    onChange={() => {
                      setFieldValue('twentyFourHourComplication', 'false');
                      setTwentyFourHourComplication('false');
                    }}
                  />
                </EpFormRadio.Wrap>
              </div>
            </div>
          ) : null}

          {values.integrationType === 2 ? (
            <div className="row">
              <div className="mb-3">
                <EpFormSelect
                  classError=""
                  customRef={firstAndTwentyFourHourModelRef}
                  optionsSelect={firstAndTwentyFourHourModelOptions}
                  inputId="firstAndTwentyFourHourModel-select"
                  placeholderText={Select}
                  inputName="firstAndTwentyFourHourModel"
                  value={values.firstAndTwentyFourHourModel}
                  onChange={(val) => { setFieldValue('firstAndTwentyFourHourModel', val.value !== undefined ? val.value : val); setFirstAndTwentyFourHourModel(val.value !== undefined ? val.value : val); }}
                  onBlur={() => setFieldTouched('firstAndTwentyFourHourModel', true)}
                  labelText={Model}
                />
              </div>
            </div>
          ) : null}

          <div className="row">
            <div className="mb-3">
              <EpSelectPagination
                customRef={hospitalIdRef}
                formatOptionLabel
                classError={errors.hospitalId && touched.hospitalId ? 'is-invalid' : ''}
                onBlur={() => setFieldTouched('hospitalId', true)}
                value={values.hospitalId}
                inputId="hospital-select"
                placeholderText={Select}
                labelText={Hospital}
                hospitalName={hospitalName}
                onFocus={document.addEventListener('keyup', handleHospitalInputChange)}
                onChange={(val) => {
                  if (val !== undefined && val !== null && val !== '') {
                    setFieldValue('hospitalId', val.value !== undefined ? val.value : hospitalObject.value);
                    setHospitalName(val.label !== undefined ? val.label : hospitalObject.label);
                    setHospitalId(val.value !== undefined ? val.value : hospitalObject.value);
                  }
                }}
                baseUrlAPI={USER_BASE_API_URL}
                endpointAPI={USER_GET_HOSPITALS_ENDPOINT}
                params={{
                  Page: 0,
                  SearchLike: hospitalInputValue,
                }}
                optionValueProp="hospitalId"
                optionLabelProp="hospitalName"
              />
              {errors.hospitalId && touched.hospitalId ? <div className="error-message">{errors.hospitalId}</div> : null}
            </div>
          </div>

          <div className="row">
            <div className="col-md mb-3">
              <EpFormInputIcon
                classError={errors.patientId ? 'is-invalid' : ''}
                value={values.patientId}
                inputId="patientId-textfield"
                inputName="patientId"
                onChange={handleChange}
                onBlur={() => setFieldTouched('patientId', true)}
                onFocus={document.addEventListener('keyup', handlepatientIdInputChange)}
                inputType="text"
                labelText={PatientId}
                placeholderText={Enter}
                inputIcon={<FaAddressBook />}
                disabled={!!(values.integrationType === 2
                    || values.integrationType === 3
                    || values.integrationType === 4
                    || values.integrationType === 5
                    || values.integrationType === 6
                    || values.integrationType === 7
                    || values.integrationType === 8
                    || values.integrationType === 9
                    || values.integrationType === ''
                    || values.integrationType === null
                    || values.integrationType === undefined)}
              />
              {errors.patientId ? <div className="error-message">{errors.patientId}</div> : null}
            </div>

            <div className="col mb-3">
              <EpFormInputIcon
                classError={errors.visitId ? 'is-invalid' : ''}
                value={values.visitId}
                inputId="visitId-textfield"
                inputName="visitId"
                onChange={handleChange}
                onFocus={document.addEventListener('keyup', handleHospitalAdmissionNumberInputChange)}
                onBlur={() => setFieldTouched('visitId', true)}
                inputType="text"
                labelText={VisitID}
                placeholderText={Enter}
                inputIcon={<FaHospitalSymbol />}
                disabled={!!(values.integrationType === 3)}
              />
              {errors.visitId ? <div className="error-message">{errors.visitId}</div> : null}
            </div>

          </div>
          <div className="row">
            <div className="mb-3">
              <EpFormSelect
                customRef={periodRef}
                classError={errors.period && touched.period ? 'is-invalid' : ''}
                onBlur={() => setFieldTouched('period', true)}
                optionsSelect={periodOptions}
                inputId="period-select"
                placeholderText={Select}
                inputName="period"
                value={values.period}
                onChange={(val) => {
                  setFieldValue('period', val.value !== undefined ? val.value : val);
                  changeDateInPeriodSelection(
                    val.value !== undefined ? val.value : val, setFieldValue, setFieldError, errors,
                  );
                  setFieldError('startDate', null);
                  setFieldError('endDate', null);
                  setFieldTouched('endDate', false);
                  setFieldTouched('startDate', false);
                }}
                labelText={SelectThePeriod}
                inputIcon={<FaCalendarAlt />}
              />
              {errors.period && touched.period ? <div className="error-message">{errors.period}</div> : null}
            </div>
          </div>

          {values.period === 3 ? (
            <div className="row">
              <div className="col-md mb-3">
                <EpFormDate
                  selected={values.startDate}
                  onChange={(date) => {
                    setFieldValue('startDate', moment(date).startOf('day')); setStartDate(moment(date).startOf('day'));
                  }}
                  selectsStart
                  selectsEnd={false}
                  startDate={values.startDate}
                  endDate={values.endDate}
                  maxDate={values.endDate ? moment(values.endDate).endOf('day') : moment(new Date()).endOf('day')}
                  minDate={moment(new Date()).startOf('day').add(-90, 'days')}
                  labelText={StartDate}
                  dateId="start-date"
                  placeholderText={Select}
                  nameDate="startDate"
                  classError={errors.startDate && touched.startDate ? 'is-invalid' : ''}
                  autoComplete="off"
                />
                {errors.startDate && touched.startDate ? <div className="error-message">{errors.startDate}</div> : null}
              </div>
              <div className="col mb-3">
                <EpFormDate
                  selected={values.endDate}
                  onChange={(date) => { setFieldValue('endDate', moment.utc(date).endOf('day')); setEndDate(moment.utc(date).endOf('day')); }}
                  selectsStart={false}
                  selectsEnd
                  startDate={values.startDate}
                  endDate={values.endDate}
                  minDate={values.startDate ? moment.utc(values.startDate) : moment.utc(new Date()).startOf('day').add(-90, 'days')}
                  maxDate={moment.utc(new Date()).endOf('day')}
                  labelText={EndDate}
                  dateId="end-date"
                  placeholderText={Select}
                  nameDate="endDate"
                  classError={errors.endDate && touched.endDate ? 'is-invalid' : ''}
                  autoComplete="off"
                />
                {errors.endDate && touched.endDate ? <div className="error-message">{errors.endDate}</div> : null}
              </div>
            </div>
          ) : ''}
        </form>
      )}
    </Formik>
  );

  if (user) {
    // Define conteúdo a ser exibido se o usuário estiver logado
    pageRendered = (
      <>

        <EpSessionTimeOutModal />
        <EpLanguageModal getTranslationsFunc={getDataTermAndTranslationsByTag} />
        <EpGlobalNavigation />

        <div className="ep__container integrations__container  ">
          <div className="integrations__container-info">
            <h2 className="integrations__container-info-title" id="page-title--integrations">{Integrations}</h2>
            <EpButton
              actionType="submit"
              labelText={Help}
              linkFunc={() => handleToggleModal('modal--integrations-help')}
              buttonType="primary"
              buttonIcon={<FaQuestionCircle className="btn__icon" />}
              customClass="btn__footer"
              disabled={false}
              size="sm"
            />
          </div>

          <EpBrowserModal />

          <EpHelpModal />

          <EpErrorModal
            tableData={errorData}
            columns={columnsErrorModal}
            modalTitle={modalErrorTitle}
            modalSubtitle={modalErrorSubtitle}
            modalErrorCount={modalErrorCount}
            loading={modalErrorLoading}
          />

          <EpSingleTableCard
            filterCancelFunc={() => cancelFilterForm()}
            filterClearFunc={() => resetFilterForm()}
            filterSubmitFunc={e => handleFilterSubmit(e)}
            filterBodyContent={filterBodyContent}
            tableData={tableData}
            columns={tableColumns}
            filterData={filterData}
            empty={(
              <EpEmpty
                title={NoFiltersAppliedToGenerateAQuery}
                description={PleaseApplyFiltersToViewIntegrationLogs}
                buttonText={ApplyFilters}
                loading={searchingData}
                linkFunc={() => handleToggleModalStick('button__dropdown-filters')}
              />
            )}
            getDataTable={getDataTable}
            integrationFuncData={integrationFuncData}
            cardTitleIntegration={cardTitleIntegration}
            currentPageNumber={currentPageNumber}
            setCurrentPageNumber={setCurrentPageNumber}
            searchingData={searchingData}
            columnsToHide={columnsToHide}
            setColumnsToHide={setColumnsToHide}
            integrationFuncName={integrationFuncName}
            lastIntegrationFiltered={lastIntegrationFiltered}
            setLastIntegrationFiltered={setLastIntegrationFiltered}
            columnsModalChecked={columnsModalChecked}
            setColumnsModalChecked={setColumnsModalChecked}
          />
        </div>
        <EpToaster duration={4000} />
      </>
    );
  }

  return (
    pageRendered
  );
};

export default IntegrationsAuditPage;
